import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { AuthService } from '../../../../core/services/auth.service';
import { catchError, filter, map, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ProfileService } from '../../../../services/profile.service';
import { FeedbackUnreadService } from '../../../../services/feedback-unread.service';
import { MessagesService } from '@shared/modules/messages';
import { AsyncPipe } from '@angular/common';
import { LocalizationPipe } from '@shared/modules/localization';
import { Breakpoints, isExternalPageUrl, resolveInternalPageUrl } from '@pkp/core';
import { LanguageSelectorComponent } from '@pkp/shared';
import { MenuService } from '@shared/core';
import { NotificationsDropdownComponent } from '../notifications-dropdown/notifications-dropdown.component';
import {
  activeLocalNavSlug,
  localNavPagePath,
} from '../../../../shared/constants/local-nav-pages.const';
import { NavMenuItem, paymentRulesNavMenus } from '../../../../shared/payment-rules/payment-rules';

@Component({
  selector: 'pkp-d-navigation-panel',
  standalone: true,
  templateUrl: './navigation-panel.component.html',
  styleUrls: ['./navigation-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    NzIconDirective,
    AsyncPipe,
    RouterLinkActive,
    NzDropDownModule,
    NzMenuModule,
    LocalizationPipe,
    LanguageSelectorComponent,
    NotificationsDropdownComponent,
  ],
})
export class NavigationPanelComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly destroy$ = inject(DestroyRef);
  private readonly profileService = inject(ProfileService);
  private readonly messageService = inject(MessagesService);
  private readonly menuService = inject(MenuService);
  private readonly router = inject(Router);
  protected readonly feedbackUnreadCount = inject(FeedbackUnreadService).count;

  /** CMS-меню: «Оплата и правила» вместо «Способов оплаты» и «Штрафов». */
  menus = computed(() => paymentRulesNavMenus(this.menuService.menuList()));

  private readonly linksContainer = viewChild<ElementRef<HTMLElement>>('linksContainer');
  private readonly linksMeasure = viewChild<ElementRef<HTMLElement>>('linksMeasure');
  private readonly visibleMenusCount = signal(Number.MAX_SAFE_INTEGER);

  visibleMenus = computed(() => this.menus().slice(0, this.visibleMenusCount()));
  overflowMenus = computed(() => this.menus().slice(this.visibleMenusCount()));
  isOverflowActive = computed(() => this.overflowMenus().some(menu => this.isMenuActive(menu)));

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(event => (event as NavigationEnd).urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );

  private readonly activeSlug = computed(() => {
    const url = this.currentUrl();
    const local = activeLocalNavSlug(url, Breakpoints.Desktop);
    if (local) {
      return local;
    }
    const match = url.match(/\/desktop\/main\/menu\/(?:page|children)\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : undefined;
  });

  isAuthenticated$ = this.authService.isAuthenticated$;
  username = signal('');
  isLoading = signal(true);

  constructor() {
    afterNextRender(() => this.observeLinksWidth());
  }

  ngOnInit(): void {
    this.isAuthenticated$
      .pipe(
        filter(isAuthenticated => isAuthenticated),
        switchMap(() => this.profileService.getProfile()),
        map(username => username?.user?.full_name ?? ''),
        catchError((err: HttpErrorResponse) => {
          const { error } = err;
          this.messageService.showError(error.message);
          return of('');
        }),
        takeUntilDestroyed(this.destroy$),
      )
      .subscribe(username => {
        this.username.set(username);
        this.isLoading.set(false);
      });
  }

  getMenuLink(menu: NavMenuItem): (string | undefined)[] | null {
    const localPath = localNavPagePath(menu.slug);
    if (localPath) {
      return ['/desktop/main', localPath];
    }
    if (menu?.children && menu?.children?.length > 0) {
      return ['/desktop/main/menu/children', menu.slug];
    }
    if (menu.page) {
      return ['/desktop/main/menu/page', menu.slug];
    }
    if (menu.pageUrl) {
      return isExternalPageUrl(menu.pageUrl)
        ? null
        : [resolveInternalPageUrl(menu.pageUrl, Breakpoints.Desktop)];
    }
    return ['/desktop/main/menu/page', menu.slug];
  }

  onMenuClick(menu: NavMenuItem, event: Event): void {
    const hasOnlyExternalUrl =
      !menu?.children?.length && !menu.page && !!menu.pageUrl && isExternalPageUrl(menu.pageUrl);
    if (hasOnlyExternalUrl && menu.pageUrl) {
      event.preventDefault();
      window.open(menu.pageUrl, '_blank', 'noopener');
    }
  }

  isMenuActive(menu: NavMenuItem): boolean {
    const slug = this.activeSlug();
    return slug ? this.containsSlug(menu, slug) : false;
  }

  private observeLinksWidth(): void {
    const container = this.linksContainer()?.nativeElement;
    const measure = this.linksMeasure()?.nativeElement;
    if (!container || !measure || typeof ResizeObserver === 'undefined') {
      return;
    }
    const observer = new ResizeObserver(() => this.updateVisibleMenusCount(container, measure));
    observer.observe(container);
    observer.observe(measure);
    this.destroy$.onDestroy(() => observer.disconnect());
  }

  private updateVisibleMenusCount(container: HTMLElement, measure: HTMLElement): void {
    const [mapWidth, ...rest] = Array.from(measure.children).map(
      child => child.getBoundingClientRect().width,
    );
    const moreWidth = rest.pop() ?? 0;
    const gap = parseFloat(getComputedStyle(measure).columnGap) || 0;
    const available = container.clientWidth;
    const fullWidth = rest.reduce((sum, width) => sum + gap + width, mapWidth ?? 0);

    if (fullWidth <= available) {
      this.visibleMenusCount.set(rest.length);
      return;
    }

    let used = (mapWidth ?? 0) + gap + moreWidth;
    let count = 0;
    for (const width of rest) {
      if (used + gap + width > available) {
        break;
      }
      used += gap + width;
      count++;
    }
    this.visibleMenusCount.set(count);
  }

  private containsSlug(menu: NavMenuItem, slug: string): boolean {
    if (menu.slug === slug) {
      return true;
    }
    return (menu.children ?? []).some(child => this.containsSlug(child, slug));
  }

  goToAuthPage(): void {
    this.router.navigate(['desktop/auth']).then();
  }

  goToRegisterPage(): void {
    this.router.navigate(['desktop/auth/register']).then();
  }
}
